/**
 * CR416: Brand text sanitization utilities for BONUS reward brand integrity.
 *
 * Strips leading currency/points/amount tokens from brand strings
 * (e.g., "$75 Fortnite" → "Fortnite", "500 pts Starbucks" → "Starbucks").
 */

/**
 * Remove leading monetary or points prefixes from brand text.
 * Examples:
 *   "$75 Fortnite"    → "Fortnite"
 *   "75 Fortnite"     → "Fortnite"
 *   "500 pts Netflix"  → "Netflix"
 *   "100 points Apple" → "Apple"
 *   "Cineplex"         → "Cineplex"  (no change)
 */
export function sanitizeBrandText(text: string | null | undefined): string | null {
  if (!text) return null;
  let cleaned = text.trim();

  // Strip leading currency amount: "$75 ", "75.00 ", "$1,500 "
  cleaned = cleaned.replace(/^\s*\$?\s*\d[\d,]*(\.\d+)?\s+/, "");

  // Strip leading points amount: "500 pts ", "1000 points "
  cleaned = cleaned.replace(/^\s*\d[\d,]*\s*(pts|points)\s+/i, "");

  cleaned = cleaned.trim();
  return cleaned.length > 0 ? cleaned : null;
}

/**
 * Check if two brand names match (case-insensitive, trimmed).
 */
export function brandsMatch(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Resolve the safe BONUS reward brand from bonus-side fields only.
 *
 * Returns { rewardBrandDisplay, rewardBrandSource } where:
 * - rewardBrandDisplay: cleaned brand string or null
 * - rewardBrandSource: provenance tag for diagnostics
 *
 * Guardrails:
 * - Never returns a value derived from purchase-side fields
 * - Strips numeric-leading tokens
 * - Nullifies if result matches purchase brand
 */
export function resolveBonusRewardBrand(
  bonusBrand: string | null | undefined,
  bonusMerchant: string | null | undefined,
  rewardBrandName: string | null | undefined,
  purchaseBrand: string | null | undefined
): { rewardBrandDisplay: string | null; rewardBrandSource: string } {
  // Try bonus-side sources in priority order
  const candidates: Array<{ value: string | null; source: string }> = [
    { value: sanitizeBrandText(bonusBrand), source: "explicit_bonus_brand" },
    { value: sanitizeBrandText(bonusMerchant), source: "explicit_bonus_merchant" },
    { value: sanitizeBrandText(rewardBrandName), source: "reward_brand_name" },
  ];

  for (const { value, source } of candidates) {
    if (!value) continue;
    // Guardrail: reject if it matches purchase brand
    if (brandsMatch(value, purchaseBrand)) continue;
    if (brandsMatch(value, sanitizeBrandText(purchaseBrand))) continue;
    return { rewardBrandDisplay: value, rewardBrandSource: source };
  }

  return { rewardBrandDisplay: null, rewardBrandSource: "unknown" };
}
